import {yupResolver} from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {Button, Form, Grid, Label, Popup, Icon, Message, Container, Radio} from 'semantic-ui-react';
import * as yup from 'yup';

import {
  wordpressSubdomain,
  validationSchemas,
  wordpressMinLength,
  siteVisibilityOptions,
} from 'components/Common/sharedElements';
import {getChangedFieldsValues} from 'forms';
import {WordPressSiteStatus, WordPressSiteSSOStatus, ProjectCategories} from 'helpers/enums';
import {useReservedHostnames} from 'hooks';

import QrCodeModal from '../../../QrCode/QrCodeModal';

import styles from './Info.module.scss';

const formSchema = yup.object().shape({
  name: validationSchemas.wordpressSiteURLValidation
    .test('domainIsUnique', 'Such domain already exists', (value, ctx) => {
      const siteDomain = value + wordpressSubdomain;
      if (siteDomain === ctx.options.context.originalUrl) {
        return true;
      }

      if (ctx.options.context.reservedHostnames.includes(value)) {
        return ctx.createError({message: 'Provided site URL is a reserved name'});
      }

      return !ctx.options.context.reservedFullyQualifiedDomainNames.includes(siteDomain);
    })
    .test(
      'minLength',
      `Minimum allowed length of a site URL is ${wordpressMinLength} characters`,
      (value, ctx) => {
        const originalSiteName = ctx.options.context.originalUrl.split('.')[0];
        if (originalSiteName !== undefined) {
          if (originalSiteName === value) {
            return true;
          } else {
            return value.length >= wordpressMinLength;
          }
        }
      }
    ),
  visibility: validationSchemas.siteVisibilityValidation,
});

export default function Info({
  url,
  onSave,
  status,
  visibility,
  version,
  ssoStatus,
  projectCategory,
}) {
  const {
    reservedHostnames,
    reservedFullyQualifiedDomainNames,
    isLoading: isLoadingReservedHostnames,
  } = useReservedHostnames('wordpress');
  const {
    control,
    handleSubmit,
    formState: {dirtyFields, isDirty, isValid, isSubmitting, isSubmitSuccessful},
    reset,
    getValues,
  } = useForm({
    context: {
      reservedHostnames,
      reservedFullyQualifiedDomainNames,
      originalUrl: url,
      visibility,
      ssoEnabled:
        ssoStatus === WordPressSiteSSOStatus.ENABLED || ssoStatus === WordPressSiteSSOStatus.FORCED,
    },
    mode: 'onChange',
    defaultValues: {
      name: url.split('.')[0],
      visibility,
      ssoEnabled:
        ssoStatus === WordPressSiteSSOStatus.ENABLED || ssoStatus === WordPressSiteSSOStatus.FORCED,
    },
    resolver: yupResolver(formSchema),
  });

  // QR code
  const [qrUrl, setQrUrl] = useState('');

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset(getValues());
    }
  }, [isSubmitSuccessful, reset, getValues]);

  const submitHandler = async data => {
    const values = getChangedFieldsValues(dirtyFields, data);
    if (values.name) {
      values.url = values.name + wordpressSubdomain;
      delete values.name;
    }

    if (onSave) {
      await onSave(values);
    }
  };

  const isTestProject = () => {
    return ProjectCategories.TEST === projectCategory;
  };

  const qrCode = url => {
    return (
      <Popup
        trigger={
          <Icon
            className={styles.QrCodeIcon}
            name="qrcode"
            onClick={() => {
              const currentUrl = `https://${url}`;
              setQrUrl(currentUrl);
            }}
          />
        }
        position="top center"
      >
        Generate QR code
      </Popup>
    );
  };

  return (
    <>
      <Container>
        {status === WordPressSiteStatus.INITIALIZING && (
          <Message warning>
            <Message.Header>
              <Icon name="warning" />
              This website is initializing
            </Message.Header>
            <p>
              Please wait a couple of minutes and refresh the page. If the process is taking a lot
              of time please open a{' '}
              <a
                href="https://cern.service-now.com/service-portal?id=functional_element&name=wordpress-infrastructure"
                target="_blank"
                rel="noreferrer"
              >
                SNOW ticket
              </a>
              .
            </p>
          </Message>
        )}
        {status === WordPressSiteStatus.BROKEN && (
          <Message warning>
            <Message.Header>
              <Icon name="warning" />
              This website has a problem
            </Message.Header>
            <p>
              You can try to recover the site by restoring it from a backup or open a{' '}
              <a
                href="https://cern.service-now.com/service-portal?id=functional_element&name=wordpress-infrastructure"
                target="_blank"
                rel="noreferrer"
              >
                SNOW ticket
              </a>
              .
            </p>
          </Message>
        )}
        {status === WordPressSiteStatus.OK && (
          <Form onSubmit={handleSubmit(submitHandler)} loading={isSubmitting}>
            {!url && <Message info>Primary hostname is not available</Message>}
            <Grid centered stackable>
              {url && (
                <>
                  <Grid.Row columns={2}>
                    <Grid.Column textAlign="right" width={8}>
                      <div className={styles.FixedLabel}>
                        <Popup
                          trigger={<label htmlFor="link">View site</label>}
                          position="left center"
                        >
                          <Popup.Content>Open your site in a separate window.</Popup.Content>
                        </Popup>
                      </div>
                    </Grid.Column>

                    <Grid.Column textAlign="left" width={8}>
                      <a
                        href={`https://${url}`}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.Url}
                      >
                        {url}
                      </a>
                      {qrCode(url)}
                    </Grid.Column>
                  </Grid.Row>

                  <Grid.Row columns={2}>
                    <Grid.Column textAlign="right" width={8}>
                      <div className={styles.FixedLabel}>
                        <Popup
                          trigger={<label htmlFor="link">Open WordPress Admin dashboard</label>}
                          position="left center"
                        >
                          <Popup.Content>Admin Dashboard</Popup.Content>
                        </Popup>
                      </div>
                    </Grid.Column>

                    <Grid.Column textAlign="left" width={8}>
                      <a
                        href={`https://${url}/wp-admin`}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.Url}
                      >
                        {url}/wp-admin
                      </a>
                      {qrCode(`${url}/wp-admin`)}
                    </Grid.Column>
                  </Grid.Row>

                  <Grid.Row columns={3}>
                    <Grid.Column textAlign="right" width={8}>
                      <div className={styles.FixedLabel}>
                        <Popup
                          trigger={<label htmlFor="name">Primary URL</label>}
                          position="left center"
                        >
                          <Popup.Content>
                            Choose descriptive and short name, following naming conventions.
                          </Popup.Content>
                        </Popup>
                      </div>
                    </Grid.Column>

                    <Grid.Column textAlign="left" width={7}>
                      <Controller
                        control={control}
                        name="name"
                        render={({field: {onChange, value, name}, fieldState: {error}}) => (
                          <Form.Input
                            id="name"
                            error={error?.message}
                            onChange={(_evt, data) => onChange(data.value)}
                            name={name}
                            value={value}
                            labelPosition="right"
                            required
                          >
                            <input />
                            <Label>{wordpressSubdomain}</Label>
                          </Form.Input>
                        )}
                      />
                    </Grid.Column>

                    <Grid.Column width={1} textAlign="left">
                      {!isDirty && qrCode(url)}
                    </Grid.Column>
                  </Grid.Row>
                </>
              )}

              {version && (
                <Grid.Row columns={2}>
                  <Grid.Column textAlign="right" width={8}>
                    <div className={styles.FixedLabel}>
                      <Popup
                        trigger={<label htmlFor="name">CERN WordPress version</label>}
                        position="left center"
                      >
                        <Popup.Content>Version of WordPress</Popup.Content>
                      </Popup>
                    </div>
                  </Grid.Column>
                  <Grid.Column textAlign="left" width={8}>
                    <div className={styles.FixedLabel}>{version}</div>
                  </Grid.Column>
                </Grid.Row>
              )}

              <Grid.Row columns={2}>
                <Grid.Column textAlign="right" width={8}>
                  <div className={styles.FixedLabel}>
                    <Popup
                      trigger={<label htmlFor="name">Visibility</label>}
                      position="left center"
                    >
                      <Popup.Content>
                        Select if your website should be visible via the internet or only inside
                        CERN.
                      </Popup.Content>
                    </Popup>
                  </div>
                </Grid.Column>
                <Grid.Column textAlign="left" width={8}>
                  <Controller
                    control={control}
                    name="visibility"
                    render={({field: {onChange, value, name}, fieldState: {error}}) => (
                      <Form.Select
                        id="visibility"
                        name={name}
                        value={value}
                        error={error?.message}
                        options={siteVisibilityOptions}
                        onChange={(_evt, data) => onChange(data.value)}
                      />
                    )}
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row columns={2}>
                <Grid.Column textAlign="right" width={8}>
                  <div className={styles.FixedLabel}>
                    <Popup
                      trigger={<label htmlFor="name">SSO enforced</label>}
                      position="left center"
                    >
                      <Popup.Content>
                        {isTestProject() ? (
                          <label htmlFor="ssoEnabled">Test websites cannot be public</label>
                        ) : (
                          <label htmlFor="ssoEnabled">
                            Select if your site should be behind SSO or public
                          </label>
                        )}
                      </Popup.Content>
                    </Popup>
                  </div>
                </Grid.Column>
                <Grid.Column verticalAlign="bottom" textAlign="left" width={8}>
                  {ssoStatus === WordPressSiteSSOStatus.FORCED ? (
                    <Message info>Test websites cannot be public</Message>
                  ) : (
                    <Controller
                      control={control}
                      name="ssoEnabled"
                      render={({field: {onChange, value, name}, fieldState: {error}}) => (
                        <Radio
                          toggle
                          disabled={ssoStatus === WordPressSiteSSOStatus.FORCED || isTestProject()}
                          name={name}
                          checked={value}
                          error={error?.message}
                          onChange={(_evt, data) => onChange(data.checked)}
                        />
                      )}
                    />
                  )}
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column textAlign="right">
                  <Button
                    type="submit"
                    className={styles.SaveButton}
                    disabled={!isValid || !isDirty || isSubmitting || isLoadingReservedHostnames}
                  >
                    Save
                  </Button>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
        )}
      </Container>
      {/* QR Code Modal */}
      {qrUrl && (
        <QrCodeModal
          siteUrl={qrUrl}
          onClose={() => {
            setQrUrl('');
          }}
        />
      )}
    </>
  );
}

Info.propTypes = {
  projectName: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  version: PropTypes.string,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  status: PropTypes.string.isRequired,
  visibility: PropTypes.string.isRequired,
  ssoStatus: PropTypes.string.isRequired,
  projectCategory: PropTypes.string.isRequired,
};
